/**
 * Anti-flicker pacing for the checkout auto-walk (bd aa14).
 *
 * `computeAutoWalkSteps` reports the path AHEAD of the flow (running = current
 * node, pending = the rest), so as the flow races through server nodes the live
 * list shrinks and the loading panel swaps content many times in well under a
 * second — the "flicker". This pure reducer decouples what the SHOPPER sees
 * from how fast the flow runs: it ACCUMULATES every step the flow passes (the
 * live list shrinks as nodes complete, so we must remember them), holds each for
 * at least `minStepMs`, settles completed steps with a checkmark, and signals
 * `reveal` only once the flow is done AND the paced display has caught up.
 * Clock-injected (`nowMs`) so the logic is testable without timers; the thin
 * `usePacedAutoWalk` hook supplies the real clock + schedules re-evaluation via
 * `nextTickMs`.
 */
import type { NarratedAutoWalkStep } from './NarratedAutoWalkProgress.js';

export interface PaceState {
  /** Every distinct step observed, in first-seen order (the live list shrinks). */
  seen: NarratedAutoWalkStep[];
  /** How many leading steps are completed; `seen[shown]` is the running step. */
  shown: number;
  /** Clock time the running step started showing; null before the first step. */
  currentSinceMs: number | null;
}

export interface PaceInput {
  /** The flow's live upcoming steps: [running, ...pending] from computeAutoWalkSteps. */
  liveSteps: NarratedAutoWalkStep[];
  /** Whether the flow is still executing a server node. */
  busy: boolean;
  /** Minimum time each step is shown before the display advances. */
  minStepMs: number;
  /** Injected clock. */
  nowMs: number;
}

export interface PaceResult {
  state: PaceState;
  completed: NarratedAutoWalkStep[];
  running: NarratedAutoWalkStep | null;
  pending: NarratedAutoWalkStep[];
  /** Reveal the real page: flow done, display caught up, final dwell met. */
  reveal: boolean;
  /** ms until the pacer should be re-evaluated, or null when nothing is pending. */
  nextTickMs: number | null;
}

export const initialPaceState: PaceState = { seen: [], shown: 0, currentSinceMs: null };

export function paceAutoWalk(prev: PaceState, input: PaceInput): PaceResult {
  const { liveSteps, busy, minStepMs, nowMs } = input;

  // 1. Accumulate newly-seen steps (the live list shrinks as the flow advances,
  //    so a step can disappear before we've displayed it — remember them all).
  const seen = [...prev.seen];
  const seenIds = new Set(seen.map((s) => s.nodeId));
  for (const s of liveSteps) {
    if (!seenIds.has(s.nodeId)) {
      seen.push(s);
      seenIds.add(s.nodeId);
    }
  }

  let shown = prev.shown;
  let currentSinceMs = prev.currentSinceMs;

  // 2. Start the dwell clock when a running step first exists.
  const hasCurrent = shown < seen.length;
  if (hasCurrent && currentSinceMs === null) currentSinceMs = nowMs;

  // 3. Advance one step once the current step has been shown for >= minStepMs.
  const dwellMet = currentSinceMs !== null && nowMs - currentSinceMs >= minStepMs;
  if (hasCurrent && dwellMet) {
    const isLast = shown + 1 >= seen.length;
    // Mid-sequence: always advance. Last step: only complete it once the flow
    // is done (while busy, hold it — more steps may still surface).
    if (!isLast || !busy) {
      shown += 1;
      currentSinceMs = shown < seen.length ? nowMs : currentSinceMs;
    }
  }

  const completed = seen.slice(0, shown);
  const running = seen[shown] ?? null;
  const pending = seen.slice(shown + 1);
  const reveal = !busy && shown >= seen.length;

  // Re-evaluate when a step is mid-dwell with more to show, or when holding the
  // final step's dwell before a (not-busy) reveal.
  const waiting = running !== null && (shown + 1 < seen.length || !busy);
  const nextTickMs =
    waiting && currentSinceMs !== null ? Math.max(0, currentSinceMs + minStepMs - nowMs) : null;

  return { state: { seen, shown, currentSinceMs }, completed, running, pending, reveal, nextTickMs };
}
